if (!customElements.get('quick-order-list')) {
  const bulkNavigationGuard = {
    lists: new Set(),
    waiting: false,
    replaying: false,
    intent: null,
    revision: 0,
    add(list) {
      this.lists.add(list);
      if (!this.listener) {
        this.listener = this.onClick.bind(this);
        this.keyListener = this.onKeydown.bind(this);
        document.addEventListener('click', this.listener, true);
        document.addEventListener('keydown', this.keyListener, true);
      }
    },
    remove(list) {
      this.lists.delete(list);
      if (this.lists.size === 0) {
        document.removeEventListener('click', this.listener, true);
        document.removeEventListener('keydown', this.keyListener, true);
        this.listener = null;
        this.keyListener = null;
        this.intent = null;
        this.revision += 1;
      }
    },
    activeLists() {
      return Array.from(this.lists).filter((list) => list.isConnected);
    },
    onClick(event) {
      if (this.replaying || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = event.target.closest?.('a[href]');
      this.captureNavigation(event, anchor);
    },
    onKeydown(event) {
      if (this.replaying || event.defaultPrevented || event.code?.toUpperCase() !== 'SPACE' || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = event.target.closest?.('a[href]');
      if (anchor?.id !== 'cart-icon-bubble' || anchor.getAttribute('role') !== 'button') return;
      this.captureNavigation(event, anchor);
    },
    captureNavigation(event, anchor) {
      if (!anchor || anchor.hasAttribute('download') || (anchor.target && anchor.target.toLowerCase() !== '_self')) return;
      const url = new URL(anchor.href, window.location.href);
      const current = new URL(window.location.href);
      if (url.origin !== window.location.origin || !['http:', 'https:'].includes(url.protocol)) return;
      if (url.pathname === current.pathname && url.search === current.search && url.href.includes('#')) return;
      const lists = this.activeLists();
      if (!this.waiting && !lists.some((list) => list.hasNavigationWork())) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.intent = { anchor, href: url.href, id: anchor.id, listId: anchor.closest('quick-order-list')?.id };
      this.revision += 1;
      if (!this.waiting) this.drain();
    },
    resolveAnchor(intent) {
      if (intent.anchor.isConnected && intent.anchor.href === intent.href) return intent.anchor;
      const byId = intent.id ? document.getElementById(intent.id) : null;
      if (byId?.href === intent.href) return byId;
      const owner = intent.listId ? document.getElementById(intent.listId) : null;
      return Array.from(owner?.querySelectorAll('a[href]') || []).find((anchor) => anchor.href === intent.href && (!anchor.target || anchor.target.toLowerCase() === '_self') && !anchor.hasAttribute('download'));
    },
    async drain() {
      this.waiting = true;
      try {
        while (this.intent) {
          const revision = this.revision;
          const lists = this.activeLists();
          if (lists.length === 0) return;
          let valid = true;
          lists.forEach((list) => { if (!list.flushNavigationQuantities()) valid = false; });
          if (!valid) throw new Error('Quantity must be corrected before navigation');
          const savingLists = lists.filter((list) => list.hasNavigationWork());
          await Promise.all(savingLists.map(async (list) => {
            try {
              await list.waitForIdle();
            } catch (error) {
              list.setBulkStatus('error');
              list.setErrorMessage(window.cartStrings.error);
              throw error;
            }
          }));
          if (!this.intent || this.activeLists().length === 0) return;
          if (savingLists.some((list) => list.isConnected && (list.bulkUpdateFailed || list.dataset.fgBulkState === 'error'))) throw new Error('Cart update was not confirmed');
          if (revision !== this.revision || this.activeLists().some((list) => list.hasNavigationWork())) continue;
          const intent = this.intent;
          this.intent = null;
          const anchor = this.resolveAnchor(intent);
          this.replaying = true;
          try {
            if (anchor) anchor.click();
            else window.location.assign(intent.href);
          } finally {
            this.replaying = false;
          }
        }
      } catch {
        this.intent = null;
      } finally {
        this.waiting = false;
      }
    },
  };
  customElements.define(
    'quick-order-list',
    class QuickOrderList extends BulkAdd {
      static navigationGuard = bulkNavigationGuard;
      cartUpdateUnsubscriber = undefined;
      hasPendingQuantityUpdate = false;
      constructor() {
        super();
        this.isListInsideModal = this.closest('bulk-modal');
        this.pendingQuantityTimers = new Map();
        this.bulkMutationRevision = 0;
        this.bulkRefreshRevision = 0;
        this.bulkSearchQuery = '';
        this.addEventListener('input', (event) => {
          if (!event.target.matches('[data-fg-bulk-search]')) return;
          this.bulkSearchQuery = event.target.value;
          this.applyBulkFilter();
        });

        this.stickyHeaderElement = document.querySelector('sticky-header');
        if (this.stickyHeaderElement) {
          this.stickyHeader = {
            height: this.stickyHeaderElement.offsetHeight,
            type: `${this.stickyHeaderElement.getAttribute('data-sticky-type')}`,
          };
        }

        this.totalBar = this.getTotalBar();
        if (this.totalBar) {
          this.totalBarPosition = window.innerHeight - this.totalBar.offsetHeight;

          this.handleResize = this.handleResize.bind(this);
          window.addEventListener('resize', this.handleResize);
        }

        this.querySelector('form').addEventListener('submit', (event) => event.preventDefault());
      }

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
          // skip if cart event was triggered by this section
          if (event.source === this.id) return;

          await this.refresh();
        });

        this.initEventListeners();
        bulkNavigationGuard.add(this);
      }

      disconnectedCallback() {
        bulkNavigationGuard.remove(this);
        this.cartUpdateUnsubscriber?.();
        window.removeEventListener('resize', this.handleResize);
        this.bulkRefreshController?.abort();
        this.pendingQuantityTimers.forEach((timer) => clearTimeout(timer));
        this.pendingQuantityTimers.clear();
      }

      handleResize() {
        this.totalBarPosition = window.innerHeight - this.totalBar.offsetHeight;
        if (this.stickyHeader) this.stickyHeader.height = this.stickyHeaderElement ? this.stickyHeaderElement.offsetHeight : 0;
      }

      initEventListeners() {
        this.querySelectorAll('.pagination__item').forEach((link) => {
          link.addEventListener('click', async (event) => {
            event.preventDefault();
            event.stopPropagation();

            const url = new URL(event.currentTarget.href);

            await this.refresh(url.searchParams.get('page') || '1');
            this.scrollTop();
          });
        });

        this.querySelector('.quick-order-list__contents').addEventListener(
          'keyup',
          this.handleScrollIntoView.bind(this)
        );

        this.quickOrderListTable.addEventListener('keydown', this.handleSwitchVariantOnEnter.bind(this));

        this.initVariantEventListeners();
        this.totalBar = this.getTotalBar();
        if (this.totalBar) this.handleResize();
        const search = this.querySelector('[data-fg-bulk-search]');
        if (search) search.value = this.bulkSearchQuery;
        this.querySelector('[data-fg-bulk-controls]')?.removeAttribute('hidden');
        this.applyBulkFilter();
      }

      initVariantEventListeners() {
        this.allInputsArray = Array.from(this.querySelectorAll('input[type="number"]'));

        this.querySelectorAll('quantity-input').forEach((qty) => {
          qty.addEventListener('change', (event) => {
            const id = event.target.dataset.index;
            clearTimeout(this.pendingQuantityTimers.get(id));
            this.hasPendingQuantityUpdate = true;
            this.setBulkStatus('saving');
            this.pendingQuantityTimers.set(id, setTimeout(() => {
              this.pendingQuantityTimers.delete(id);
              this.hasPendingQuantityUpdate = this.pendingQuantityTimers.size > 0;
              this.onChange(event);
            }, BulkAdd.ASYNC_REQUEST_DELAY));
          });
        });

        this.querySelectorAll('.quick-order-list-remove-button').forEach((button) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            this.removeVariant(button.dataset.index);
          });
        });
      }

      removeVariant(variantId) {
        const id = String(variantId);
        clearTimeout(this.pendingQuantityTimers.get(id));
        this.pendingQuantityTimers.delete(id);
        this.hasPendingQuantityUpdate = this.pendingQuantityTimers.size > 0;
        this.queue = this.queue.filter((item) => String(item.id) !== id);
        const input = this.querySelector(`#Quantity-${id}`);
        if (input) {
          input.value = '0';
          input.setAttribute('value', '0');
          input.setCustomValidity('');
        }
        this.toggleLoading(true);
        this.startQueue(id, 0);
      }

      get currentPage() {
        return this.querySelector('.pagination-wrapper')?.dataset?.page ?? '1';
      }

      get cartVariantsForProduct() {
        return JSON.parse(this.querySelector('[data-cart-contents]')?.innerHTML || '[]');
      }

      getCartQuantityForLine(id) {
        const cartQuantity = super.getCartQuantityForLine(id);
        if (cartQuantity > 0) return cartQuantity;

        return this.cartVariantsForProduct.includes(parseInt(id, 10)) ? 1 : 0;
      }

      onChange(event) {
        const inputValue = Number(event.target.value);
        this.cleanErrorMessageOnType(event);
        if (event.target.value.trim() === '' || !Number.isInteger(inputValue) || inputValue < 0) {
          event.target.setCustomValidity(window.cartStrings.error);
          event.target.reportValidity();
          this.setBulkStatus('error');
          return;
        }
        if (inputValue == 0) {
          event.target.setAttribute('value', inputValue);
          this.startQueue(event.target.dataset.index, inputValue);
        } else {
          this.validateQuantity(event);
          if (!this.validateInput(event.target)) this.setBulkStatus('error');
        }
      }

      cleanErrorMessageOnType(event) {
        const handleKeydown = () => {
          event.target.setCustomValidity(' ');
          event.target.reportValidity();
          event.target.removeEventListener('keydown', handleKeydown);
        };

        event.target.addEventListener('keydown', handleKeydown);
      }

      validateInput(target) {
        const targetValue = parseInt(target.value);
        const targetMin = parseInt(target.dataset.min);
        const targetStep = parseInt(target.step);

        if (target.max) {
          return (
            targetValue == 0 ||
            (targetValue >= targetMin && targetValue <= parseInt(target.max) && targetValue % targetStep == 0)
          );
        } else {
          return targetValue == 0 || (targetValue >= targetMin && targetValue % targetStep == 0);
        }
      }

      get quickOrderListTable() {
        return this.querySelector('.quick-order-list__table');
      }

      getSectionsToRender() {
        return [
          {
            id: this.id,
            section: this.dataset.section,
            selector: `#${this.id}`,
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '#shopify-section-cart-icon-bubble',
          },
          {
            id: `quick-order-list-live-region-text-${this.dataset.productId}`,
            section: 'cart-live-region-text',
            selector: '.shopify-section',
          },
          {
            id: 'CartDrawer',
            selector: '.drawer__inner',
            section: 'cart-drawer',
          },
        ];
      }

      toggleTableLoading(enable) {
        this.quickOrderListTable.classList.toggle('quick-order-list__container--disabled', enable);
        this.toggleLoading(enable);
      }

      startQueue(id, quantity) {
        this.bulkMutationRevision += 1;
        this.setBulkStatus('saving');
        super.startQueue(id, quantity);
      }

      setBulkStatus(state) {
        this.dataset.fgBulkState = state;
        const status = this.querySelector('[data-fg-bulk-status]');
        if (status) status.textContent = this.dataset[`status${state[0].toUpperCase()}${state.slice(1)}`] || '';
        this.setAttribute('aria-busy', String(state === 'saving'));
      }

      normalizeBulkSearch(value) {
        return value.toLocaleLowerCase().replace(/\bus\s*(\d+(?:\.\d+)?)/g, 'us $1').replace(/(\d+(?:\.\d+)?)\s*(ct|mm)\b/g, '$1$2').trim();
      }

      applyBulkFilter() {
        if (!this.hasAttribute('data-fg-bulk')) return;
        const terms = this.normalizeBulkSearch(this.bulkSearchQuery).match(/us \d+(?:\.\d+)?|[^\s]+/g) || [];
        const rows = Array.from(this.querySelectorAll('tr[data-fg-search]'));
        let count = 0;
        rows.forEach((row) => {
          const text = this.normalizeBulkSearch(row.dataset.fgSearch);
          row.hidden = !terms.every((term) => {
            if (/^us \d/.test(term) || /^\d+(?:\.\d+)?(?:ct|mm)$/.test(term)) {
              const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              return new RegExp(`(^|[^a-z0-9.])${escaped}(?![a-z0-9.])`).test(text);
            }
            return text.includes(term);
          });
          const errorRow = row.nextElementSibling;
          if (errorRow?.classList.contains('desktop-row-error')) errorRow.hidden = row.hidden;
          if (!row.hidden) count += 1;
        });
        this.querySelector('[data-fg-bulk-empty]')?.toggleAttribute('hidden', count !== 0);
        const output = this.querySelector('[data-fg-bulk-count]');
        if (output) output.textContent = (this.dataset.countLabel || '').replace('__COUNT__', count).replace('__TOTAL__', rows.length);
        this.allInputsArray = Array.from(this.querySelectorAll('tr[data-fg-search]:not([hidden]) input[type="number"]'));
      }

      navigationInputs() {
        return Array.from(this.querySelectorAll('input[type="number"][data-index]'));
      }

      hasUncommittedNavigationValue(input) {
        const current = input.value.trim();
        const previous = input.getAttribute('value');
        if (current === '' || previous === null || previous.trim() === '') return current !== previous;
        const value = Number(current);
        const committedValue = Number(previous);
        return Number.isFinite(value) && Number.isFinite(committedValue) ? value !== committedValue : current !== previous;
      }

      hasNavigationWork() {
        return this.hasPendingQuantityUpdate || this.pendingQuantityTimers.size > 0 || this.queue.length > 0 || this.requestStarted || this.navigationInputs().some((input) => this.hasUncommittedNavigationValue(input));
      }

      flushNavigationQuantities() {
        let valid = true;
        this.navigationInputs().forEach((input) => {
          const id = input.dataset.index;
          clearTimeout(this.pendingQuantityTimers.get(id));
          this.pendingQuantityTimers.delete(id);
          if (!this.hasUncommittedNavigationValue(input)) return;
          const value = Number(input.value);
          if (input.value.trim() === '' || !Number.isInteger(value) || value < 0 || !this.validateInput(input)) {
            input.setCustomValidity(window.cartStrings.error);
            input.reportValidity();
            this.setBulkStatus('error');
            this.setErrorMessage(window.cartStrings.error);
            valid = false;
            return;
          }
          this.onChange({ target: input });
        });
        this.hasPendingQuantityUpdate = this.pendingQuantityTimers.size > 0;
        if (!this.hasPendingQuantityUpdate && !this.queue.length && !this.requestStarted && this.dataset.fgBulkState === 'saving') {
          this.setBulkStatus(this.bulkUpdateFailed ? 'error' : 'saved');
        }
        return valid;
      }

      async waitForIdle() {
        const started = Date.now();
        while (this.isConnected && (this.hasPendingQuantityUpdate || this.queue.length || this.requestStarted)) {
          if (Date.now() - started > 20000) throw new Error('Cart update is still pending');
          await new Promise((resolve) => setTimeout(resolve, 30));
        }
      }

      async removeAllProductVariants() {
        try {
          await this.waitForIdle();
          if (!this.isConnected) return;
          const items = this.cartVariantsForProduct.reduce((acc, id) => ({ ...acc, [id]: 0 }), {});
          if (!Object.keys(items).length) return;
          this.setRequestStarted(true);
          await this.updateMultipleQty(items);
        } catch (error) {
          this.setErrorMessage(window.cartStrings.error);
          this.setBulkStatus('error');
        }
      }

      async refresh(pageNumber = null) {
        const request = ++this.bulkRefreshRevision;
        this.bulkRefreshController?.abort();
        this.bulkRefreshController = new AbortController();
        const controller = this.bulkRefreshController;
        const page = pageNumber || this.currentPage;
        const url = new URL(this.dataset.url || window.location.pathname, window.location.origin);
        url.searchParams.set('section_id', this.dataset.section);
        url.searchParams.set('page', page);
        try {
          for (;;) {
            await this.waitForIdle();
            if (!this.isConnected || request !== this.bulkRefreshRevision) return;
            this.toggleTableLoading(true);
            const revision = this.bulkMutationRevision;
            const response = await fetch(url.toString(), { signal: controller.signal });
            if (!response.ok) throw new Error('Option list could not be refreshed');
            const responseText = await response.text();
            if (!this.isConnected || request !== this.bulkRefreshRevision) return;
            if (this.hasPendingQuantityUpdate || this.queue.length || this.requestStarted || revision !== this.bulkMutationRevision) continue;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const responseQuickOrderList = html.querySelector(`#${this.id}`);
            if (!responseQuickOrderList || responseQuickOrderList.dataset.productId !== this.dataset.productId) throw new Error('Option list identity mismatch');
            this.innerHTML = responseQuickOrderList.innerHTML;
            this.initEventListeners();
            this.setBulkStatus('saved');
            break;
          }
        } catch (error) {
          if (error.name !== 'AbortError' && request === this.bulkRefreshRevision) {
            this.setErrorMessage(window.cartStrings.error);
            this.setBulkStatus('error');
          }
        } finally {
          if (request === this.bulkRefreshRevision) this.toggleTableLoading(false);
        }
      }

      renderSections(parsedState) {
        const { items, sections } = parsedState;

        this.getSectionsToRender().forEach(({ id, selector, section }) => {
          const sectionElement = document.getElementById(id);
          if (!sectionElement) return;

          const newSection = new DOMParser().parseFromString(sections[section], 'text/html').querySelector(selector);

          if (section === this.dataset.section) {
            if (this.queue.length > 0 || this.hasPendingQuantityUpdate) return;

            const focusedElement = document.activeElement;
            let focusTarget = focusedElement?.dataset?.target;
            if (focusTarget?.includes('remove')) {
              focusTarget = focusedElement.closest('quantity-popover')?.querySelector('[data-target*="increment-"]')
                ?.dataset.target;
            }

            const total = this.getTotalBar();
            if (total) {
              total.innerHTML = newSection.querySelector('.quick-order-list__total').innerHTML;
            }

            const table = this.quickOrderListTable;
            const newTable = newSection.querySelector('.quick-order-list__table');

            // only update variants if they are from the active page
            const shouldUpdateVariants =
              this.currentPage === (newSection.querySelector('.pagination-wrapper')?.dataset.page ?? '1');
            if (newTable && shouldUpdateVariants) {
              table.innerHTML = newTable.innerHTML;

              const newFocusTarget = this.querySelector(`[data-target='${focusTarget}']`);
              if (newFocusTarget) {
                newFocusTarget?.focus({ preventScroll: true });
              }

              this.initVariantEventListeners();
              this.applyBulkFilter();
            }
          } else if (section === 'cart-drawer') {
            sectionElement.closest('cart-drawer')?.classList.toggle('is-empty', items.length === 0);
            sectionElement.querySelector(selector).innerHTML = newSection.innerHTML;
          } else {
            sectionElement.innerHTML = newSection.innerHTML;
          }
        });
      }

      getTotalBar() {
        return this.querySelector('.quick-order-list__total');
      }

      scrollTop() {
        const { top } = this.getBoundingClientRect();

        if (this.isListInsideModal) {
          this.scrollIntoView();
        } else {
          window.scrollTo({ top: top + window.scrollY - (this.stickyHeader?.height || 0), behavior: 'instant' });
        }
      }

      scrollQuickOrderListTable(target) {
        const inputTopBorder = target.getBoundingClientRect().top;
        const inputBottomBorder = target.getBoundingClientRect().bottom;

        if (this.isListInsideModal) {
          const totalBarCrossesInput = inputBottomBorder > this.totalBar.getBoundingClientRect().top;
          const tableHeadCrossesInput =
            inputTopBorder < this.querySelector('.quick-order-list__table thead').getBoundingClientRect().bottom;

          if (totalBarCrossesInput || tableHeadCrossesInput) {
            this.scrollToCenter(target);
          }
        } else {
          const stickyHeaderBottomBorder = this.stickyHeaderElement?.getBoundingClientRect().bottom;
          const totalBarCrossesInput = inputBottomBorder > this.totalBarPosition;
          const inputOutsideOfViewPort =
            inputBottomBorder < this.querySelector('.variant-item__quantity-wrapper').offsetHeight;
          const stickyHeaderCrossesInput =
            this.stickyHeaderElement &&
            this.stickyHeader.type !== 'on-scroll-up' &&
            this.stickyHeader.height > inputTopBorder;
          const stickyHeaderScrollupCrossesInput =
            this.stickyHeaderElement &&
            this.stickyHeader.type === 'on-scroll-up' &&
            this.stickyHeader.height > inputTopBorder &&
            stickyHeaderBottomBorder > 0;

          if (
            totalBarCrossesInput ||
            inputOutsideOfViewPort ||
            stickyHeaderCrossesInput ||
            stickyHeaderScrollupCrossesInput
          ) {
            this.scrollToCenter(target);
          }
        }
      }

      scrollToCenter(target) {
        target.scrollIntoView({
          block: 'center',
          behavior: 'smooth',
        });
      }

      handleScrollIntoView(event) {
        if ((event.key === 'Tab' || event.key === 'Enter') && this.allInputsArray.length !== 1) {
          this.scrollQuickOrderListTable(event.target);
        }
      }

      handleSwitchVariantOnEnter(event) {
        if (event.key !== 'Enter' || event.target.tagName !== 'INPUT') return;

        event.preventDefault();
        event.target.blur();

        if (!this.validateInput(event.target) || this.allInputsArray.length <= 1) return;

        const currentIndex = this.allInputsArray.indexOf(event.target);
        const offset = event.shiftKey ? -1 : 1;
        const nextIndex = (currentIndex + offset + this.allInputsArray.length) % this.allInputsArray.length;

        this.allInputsArray[nextIndex]?.select();
      }

      updateMultipleQty(items) {
        this.hasPendingQuantityUpdate = this.pendingQuantityTimers.size > 0;

        this.toggleLoading(true);
        this.bulkMutationRevision += 1;
        this.bulkUpdateFailed = false;
        this.setBulkStatus('saving');
        const url = this.dataset.url || window.location.pathname;
        const linesUpdate = this.startCartLinesUpdate(items);

        const body = JSON.stringify({
          updates: items,
          sections: this.getSectionsToRender().map(({ section }) => section),
          sections_url: `${url}?page=${this.currentPage}`,
        });

        this.updateMessage();
        this.setErrorMessage();

        return fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
          .then((response) => response.json())
          .then((parsedState) => {
            if (parsedState.errors || parsedState.status >= 400) {
              throw Object.assign(new Error(parsedState.description || parsedState.errors || window.cartStrings.error), { code: 'INVALID' });
            }

            linesUpdate?.resolve(parsedState);
            this.renderSections(parsedState);
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: this.id,
              cartData: parsedState,
            });
          })
          .catch((e) => {
            this.bulkUpdateFailed = true;
            this.setBulkStatus('error');
            if (e.code !== 'INVALID') console.error(e);
            this.dispatchCartErrorEvent(
              e.code === 'INVALID' ? e.message : window.cartStrings.error,
              e.code || 'SERVICE_UNAVAILABLE'
            );
            linesUpdate?.reject(e);
            this.setErrorMessage(e.code === 'INVALID' ? e.message : window.cartStrings.error);
          })
          .finally(() => {
            this.queue.length === 0 && this.toggleLoading(false);
            this.setRequestStarted(false);
            if (!this.queue.length && !this.hasPendingQuantityUpdate && !this.bulkUpdateFailed) this.setBulkStatus('saved');
          });
      }

      setErrorMessage(message = null) {
        this.errorMessageTemplate =
          this.errorMessageTemplate ??
          document.getElementById(`QuickOrderListErrorTemplate-${this.dataset.productId}`).cloneNode(true);
        const errorElements = this.querySelectorAll('.quick-order-list-error');

        errorElements.forEach((errorElement) => {
          errorElement.innerHTML = '';
          if (!message) return;
          const updatedMessageElement = this.errorMessageTemplate.cloneNode(true);
          updatedMessageElement.content.querySelector('.quick-order-list-error-message').innerText = message;
          errorElement.appendChild(updatedMessageElement.content);
        });
      }

      updateMessage(quantity = null) {
        const messages = this.querySelectorAll('.quick-order-list__message-text');
        const icons = this.querySelectorAll('.quick-order-list__message-icon');

        if (quantity === null || isNaN(quantity)) {
          messages.forEach((message) => (message.innerHTML = ''));
          icons.forEach((icon) => icon.classList.add('hidden'));
          return;
        }

        const isQuantityNegative = quantity < 0;
        const absQuantity = Math.abs(quantity);

        const textTemplate = isQuantityNegative
          ? absQuantity === 1
            ? window.quickOrderListStrings.itemRemoved
            : window.quickOrderListStrings.itemsRemoved
          : quantity === 1
          ? window.quickOrderListStrings.itemAdded
          : window.quickOrderListStrings.itemsAdded;

        messages.forEach((msg) => (msg.innerHTML = textTemplate.replace('[quantity]', absQuantity)));

        if (!isQuantityNegative) {
          icons.forEach((i) => i.classList.remove('hidden'));
        }
      }

      updateError(updatedValue, id) {
        let message = '';
        if (typeof updatedValue === 'undefined') {
          message = window.cartStrings.error;
        } else {
          message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
        }
        this.updateLiveRegions(id, message);
      }

      updateLiveRegions(id, message) {
        const variantItemErrorMobile = document.getElementById(`Quick-order-list-item-error-mobile-${id}`);
        const variantItemErrorDesktop = document.getElementById(`Quick-order-list-item-error-desktop-${id}`);
        if (variantItemErrorDesktop) {
          variantItemErrorDesktop.querySelector('.variant-item__error-text').innerHTML = message;
          variantItemErrorDesktop.closest('tr').classList.remove('hidden');
        }
        if (variantItemErrorMobile)
          variantItemErrorMobile.querySelector('.variant-item__error-text').innerHTML = message;

        this.querySelector('#shopping-cart-variant-item-status').setAttribute('aria-hidden', true);

        const cartStatus = document.getElementById(`quick-order-list-live-region-text-${this.dataset.productId}`);
        cartStatus.setAttribute('aria-hidden', false);

        setTimeout(() => {
          cartStatus.setAttribute('aria-hidden', true);
        }, 1000);
      }

      toggleLoading(loading, target = this) {
        target.querySelector('#shopping-cart-variant-item-status').toggleAttribute('aria-hidden', !loading);
        target
          .querySelectorAll('.variant-remove-total .loading__spinner')
          ?.forEach((spinner) => spinner.classList.toggle('hidden', !loading));
      }
    }
  );
}

if (!customElements.get('quick-order-list-remove-all-button')) {
  customElements.define(
    'quick-order-list-remove-all-button',
    class QuickOrderListRemoveAllButton extends HTMLElement {
      constructor() {
        super();
        this.quickOrderList = this.closest('quick-order-list');

        this.actions = {
          confirm: 'confirm',
          remove: 'remove',
          cancel: 'cancel',
        };

        this.addEventListener('click', (event) => {
          event.preventDefault();
          if (this.dataset.action === this.actions.confirm) {
            this.toggleConfirmation(false, true);
          } else if (this.dataset.action === this.actions.remove) {
            this.quickOrderList.removeAllProductVariants();
            this.toggleConfirmation(true, false);
          } else if (this.dataset.action === this.actions.cancel) {
            this.toggleConfirmation(true, false);
          }
        });
      }

      toggleConfirmation(showConfirmation, showInfo) {
        this.quickOrderList
          .querySelector('.quick-order-list-total__confirmation')
          .classList.toggle('hidden', showConfirmation);
        this.quickOrderList.querySelector('.quick-order-list-total__info').classList.toggle('hidden', showInfo);
      }
    }
  );
}
